import { getEntries } from 'src/redux/utils'

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Workflow } from '../../consts'
import { AsyncTask } from '../async-processing/consts'
import { addAsyncExtraReducers } from '../async-processing/utils'
import { addErrorExtraReducers } from '../error/utils'
import {
  AdoptionConfigMode,
  Breakdown,
  BreakdownConfig,
  EndUseParameterConfig,
  EndUses,
  FuelParameter,
  FuelParameterConfig,
  FuelParameters,
  FuelSource,
  FuelSources,
  GraphType,
  MaxVizFileUploads,
  Metric,
  ScenarioSettingsPage,
  SettingsPage,
  SolarParameterEntries,
  TimeSeriesMode,
} from './consts'
import {
  AnalysisState,
  ConfirmSettingsPayload,
  EndUseParameterSettings,
  EndUseSettings,
  FuelParameterTimeSeries,
  FuelSettings,
  FuelSourceSettings,
  FuelSourceTimeSeries,
  HandleAdoptionSuccessPayload,
  HandleVizfileDataPayload,
  ScenarioSettingsConfirmedKeys,
  SetAdoptionModePayload,
  SetAdoptionOccupationPayload,
  SetAdoptionProbabilityPayload,
  SetBaselineAssignmentPayload,
  SetBreakdownPayload,
  SetEcmAssignmentPayload,
  SetGraphModePayload,
  SetMetricPayload,
  SetNormalizedPayload,
  SetScenarioNamePayload,
  SetSelectedScenarioPayload,
  SetTargetMetricPayload,
  SetTargetPayload,
  SetTaskUrlPaylaod,
  SettingsPageSettings,
  SolarSettings,
  UmiSDLCommon,
  UpdateEndUseScenarioSettingsPayload,
  UpdateFuelScenarioSettingsPayload,
  UpdateSettingsPageGroupPayload,
  UpdateSettingsPageTabPayload,
  UpdateSolarScenarioSettingsPayload,
  UpdateTimeSeriesSettingPayload,
  UpdateTracksBaselinePayload,
} from './types'

const workflow = Workflow.analysis

const buildInitialSettingsPageSettings = (): SettingsPageSettings => {
  return {
    scenarioSettingsGroup: ScenarioSettingsPage.EndUses,
    tab: SettingsPage.Targets,
  }
}

const buildInitialTimeSeriesSettings = (): FuelParameterTimeSeries => {
  const timeSeriesSettings = {} as FuelParameterTimeSeries
  FuelParameters.forEach((parameterKey) => {
    timeSeriesSettings[parameterKey] = {} as FuelSourceTimeSeries
    FuelSources.forEach((fuelSource) => {
      timeSeriesSettings[parameterKey][fuelSource] = {
        mode: TimeSeriesMode.Exponential,
        rate: 0.0,
        list: [],
      }
    })
  })
  return timeSeriesSettings
}

const buildInitialSolarSettings = (): SolarSettings => {
  const settings = {} as SolarSettings
  SolarParameterEntries.forEach(([parameter, { defaultValue }]) => {
    // TODO: This is just to prevent a type error,
    // parameter and defaultValue should always align to hav the correct vals
    settings[parameter as 'Coverage'] = defaultValue as number
  })
  return settings
}

const initialState: AnalysisState = {
  fromCloud: false,
  selectedMetric: Metric.Carbon,
  selectedBreakdown: Breakdown.Total,
  selectedScenario: undefined,
  graphMode: GraphType.bar,
  normalized: false,
  targetsConfirmed: false,
  targets: {
    metric: Metric.Carbon,
    near: {
      value: 25,
      year: 2035,
    },
    long: {
      value: 5,
      year: 2060,
    },
  },
  timeSeriesConfirmed: false,
  timeSeriesSettings: buildInitialTimeSeriesSettings(), // TODO: this was a spread operator make sure it still works
  settingsPage: buildInitialSettingsPageSettings(),
  // Settings related to uploaded files / computed data
  filenames: Array(MaxVizFileUploads)
    .fill(0)
    .map((_, i) => ''),
  scenarioNames: Array(MaxVizFileUploads)
    .fill(0)
    .map((_, i) => `Scenario ${i + 1}`), // user editable scenario names
  data: null,
  reports: null,
  settings: {}, // settings per scenario
  settingsConfirmed: {},
  adoption: {
    metric: null,
    mode: AdoptionConfigMode.Fixed,
    data: null,
    cacheKey: false,
    nameColumn: null,
    assignments: {
      baseline: null,
      ecms: [null, null],
    },
    probabilities: {
      value: [0.05, 0.05],
      data: null,
    },
    occupation: {
      value: 1,
      data: null as number | null,
    },
    iterations: 20,
  },
  taskUrl: undefined,
  isProcessing: false,
  error: null,
  errors: {},
  runtimeError: undefined,
}

export const analysisSlice = createSlice({
  name: workflow,
  initialState,
  reducers: {
    setScenarioName: (state, { payload }: SetScenarioNamePayload) => {
      state.scenarioNames[payload.index] = payload.name
    },
    setUploadFilename: (state, { payload }: SetScenarioNamePayload) => {
      state.filenames[payload.index] = payload.name
    },
    setTaskUrl: (state, { payload }: SetTaskUrlPaylaod) => {
      state.taskUrl = payload
    },
    loadData: (state, { payload }) => {},
    clearData: (state) => {
      handleClearData(state)
    },
    clearReports: (state) => {
      state.reports = null
    },
    confirmSettings: (state, { payload }: ConfirmSettingsPayload) => {
      if (payload.all) {
        state.timeSeriesConfirmed = true
        state.targetsConfirmed = true
        Object.keys(state.settingsConfirmed).forEach((scenario) => {
          Object.keys(state.settingsConfirmed[scenario]).forEach((group) => {
            state.settingsConfirmed[scenario][group as ScenarioSettingsConfirmedKeys] = true
          })
        })
        return
      }
      switch (payload.settingsGroup) {
        case SettingsPage.TimeSeries:
          state.timeSeriesConfirmed = true
          return
        case SettingsPage.Targets:
          state.targetsConfirmed = true
          return
        default:
          if (!payload.settingsGroup || !payload.scenarioSettingsGroup) return
          state.settingsConfirmed[payload.settingsGroup][payload.scenarioSettingsGroup] = true
          return
      }
    },
    updateFuelScenarioSettings: (state, { payload }: UpdateFuelScenarioSettingsPayload) => {
      const { scenarioName, parameterKey, fuelSource, value } = payload
      state.settings[scenarioName].fuels[parameterKey][fuelSource] = value
      state.adoption.cacheKey = false
    },
    updateEndUseScenarioSettings: (state, { payload }: UpdateEndUseScenarioSettingsPayload) => {
      const { scenarioName, endUse, parameter, value } = payload
      // TODO: prevents FuelSourceKeys | number from causing a TS error
      state.settings[scenarioName].endUses[endUse][parameter as 'CoP'] = value as number
      state.adoption.cacheKey = false
    },
    updateSolarScenarioSettings: (state, { payload }: UpdateSolarScenarioSettingsPayload) => {
      const { scenarioName, parameter, value } = payload
      // TODO: prevents boolean | number from causing a TS error
      state.settings[scenarioName].solar[parameter as 'Coverage'] = value as number
      state.adoption.cacheKey = false
    },
    setMetric: (state, { payload }: SetMetricPayload) => {
      state.selectedMetric = payload
    },
    setBreakdown: (state, { payload }: SetBreakdownPayload) => {
      state.selectedBreakdown = payload
    },
    setNormalized: (state, { payload }: SetNormalizedPayload) => {
      state.normalized = payload
    },
    setSelectedScenario: (state, { payload }: SetSelectedScenarioPayload) => {
      state.selectedScenario = payload
      if (payload === null || payload === undefined) {
        state.selectedBreakdown = Breakdown.Total
        state.normalized = false
      }
    },
    setGraphMode: (state, { payload }: SetGraphModePayload) => {
      state.graphMode = payload
    },
    setTarget: (state, { payload }: SetTargetPayload) => {
      state.targets[payload.target][payload.parameter] = payload.value
    },
    setTargetMetric: (state, { payload }: SetTargetMetricPayload) => {
      state.targets.metric = payload
    },
    updateTimeSeriesSetting: (state, { payload }: UpdateTimeSeriesSettingPayload) => {
      // As statements prevent type error, payload enforces type safety
      state.timeSeriesSettings[payload.fuelParameter][payload.fuelSource][
        payload.parameter as 'rate'
      ] = payload.value as number
      state.adoption.cacheKey = false
    },
    updateSettingsPageTab: (state, { payload }: UpdateSettingsPageTabPayload) => {
      if (payload) {
        state.settingsPage.tab = payload
      }
    },
    updateScenarioSettingsPageGroup: (state, { payload }: UpdateSettingsPageGroupPayload) => {
      if (payload) {
        state.settingsPage.scenarioSettingsGroup = payload
      }
    },
    updateTracksBaseline: (state, { payload }: UpdateTracksBaselinePayload) => {
      state.settings[payload.scenarioName].tracksBaseline[payload.settingsGroup] = payload.value
    },
    // TODO: Adoption baseline should be coupled with "global" baseline,
    // which is just 0 index scenario
    setBaselineAssignment: (state, { payload }: SetBaselineAssignmentPayload) => {
      state.adoption.assignments.baseline = payload
      state.adoption.cacheKey = false
    },
    setEcmAssignment: (state, { payload }: SetEcmAssignmentPayload) => {
      state.adoption.assignments.ecms[payload.index] = payload.scenario
      state.adoption.cacheKey = false
    },
    setAdoptionOccupation: (state, { payload }: SetAdoptionOccupationPayload) => {
      state.adoption.occupation.value = payload.value
      state.adoption.cacheKey = false
    },
    setAdoptionProbability: (state, { payload }: SetAdoptionProbabilityPayload) => {
      state.adoption.probabilities.value[payload.index] = payload.value
      state.adoption.cacheKey = false
    },
    setAdoptionMode: (state, { payload }: SetAdoptionModePayload) => {
      state.adoption.mode = payload
      state.adoption.cacheKey = false
      switch (payload) {
        case AdoptionConfigMode.GIS:
          state.adoption.occupation.value = null
          state.adoption.probabilities.value.forEach(
            (_, i) => (state.adoption.probabilities.value[i] = null),
          )
          break
        case AdoptionConfigMode.Fixed:
          state.adoption.occupation.value = 1
          state.adoption.probabilities.value.forEach(
            (_, i) => (state.adoption.probabilities.value[i] = 0.05),
          )
          break
      }
    },
    setAdoptionGisIdColumn: (state, { payload }) => {
      state.adoption.nameColumn = payload.value
      state.adoption.cacheKey = false
    },
    setAdoptionCacheKey: (state, { payload }) => {
      state.adoption.cacheKey = payload
      state.adoption.metric = payload
    },
  },
  extraReducers: (builder) => {
    addErrorExtraReducers(builder, { workflow })
    addAsyncExtraReducers(builder, { workflow, handlers })
  },
})

const handleVizFileData = (state: AnalysisState, { payload }: HandleVizfileDataPayload) => {
  const { data, reports, fromCloud, targets } = payload.result
  state.fromCloud = fromCloud
  if (targets) {
    state.targets = {
      near: {
        ...targets.near,
      },
      long: {
        ...targets.long,
      },
      metric: targets.metric,
    }
  }
  state.data = {}
  state.settings = {}
  state.settingsConfirmed = {}
  state.settingsPage = buildInitialSettingsPageSettings()
  state.adoption.cacheKey = false
  getEntries(data).forEach(
    ([
      scenario,
      {
        settings,
        sort_order: sortOrder,
        archetype,
        archetype_normalized,
        use,
        use_normalized,
        analysis_settings,
      },
    ]) => {
      switch (sortOrder) {
        case 0:
          state.selectedScenario = scenario
          state.adoption.assignments.baseline = scenario
          break
        case 1:
          state.adoption.assignments.ecms[0] = scenario
          break
        case 2:
          state.adoption.assignments.ecms[1] = scenario
          break
      }

      // Extract just the desired values
      if (!state.data) state.data = {}
      state.data[scenario] = {
        Archetype: {
          normalized: archetype_normalized,
          original: archetype,
        },
        Use: { normalized: use_normalized, original: use },
        sortOrder,
      }
      // Load default fuel settings from sdl-common/project-settings.json
      const fuels = {} as FuelSettings
      getEntries(FuelParameterConfig).forEach(([parameterKey, { umiSuffix }]) => {
        const parameterSettings = {} as FuelSourceSettings
        FuelSources.forEach((fuelSource) => {
          const key = `${fuelSource}${umiSuffix}` as keyof UmiSDLCommon['project-settings']
          parameterSettings[fuelSource] = settings[key]
        })
        fuels[parameterKey] = parameterSettings
      })

      // Initialize default values for endUses
      const endUses = {} as EndUseSettings
      EndUses.forEach((endUseKey) => {
        const endUse = {} as EndUseParameterSettings
        getEntries(EndUseParameterConfig).forEach(([parameterKey, { defaultValue, usedBy }]) => {
          if (usedBy.includes(endUseKey)) {
            // As casts to deal with polymorphism
            endUse[parameterKey as 'CoP'] = defaultValue as number
          }
        })
        endUses[endUseKey] = endUse
      })

      state.settings[scenario] = {
        fuels,
        endUses,
        solar: buildInitialSolarSettings(),
        tracksBaseline: {
          PV: sortOrder !== 0,
          Fuels: sortOrder !== 0,
          EndUses: sortOrder !== 0,
        },
      }
      state.settingsConfirmed[scenario] = {
        PV: false,
        EndUses: false,
        Fuels: false,
      }

      // If analysis settings are present from cloud response, overwrite defaults
      if (analysis_settings) {
        console.log('Found analysis settings in results delivered from processor')
        state.settings[scenario] = analysis_settings
      }
    },
  )
  state.reports = reports
}

const handleAdoptionSuccess = (state: AnalysisState, { payload }: HandleAdoptionSuccessPayload) => {
  state.adoption.data = payload.result
}

const handleClearAdoption = (state: AnalysisState) => {
  state.adoption.data = null
}

const handleClearData = (state: AnalysisState) => {
  state.reports = null
  state.data = null
  state.settingsConfirmed = {}
  state.settings = {}
  state.selectedScenario = undefined
  state.settingsPage = buildInitialSettingsPageSettings()
  state.adoption = initialState.adoption
}

const handlers = {
  [AsyncTask.computeAdoption]: {
    onStart: handleClearAdoption,
    onSuccess: handleAdoptionSuccess,
  },
  [AsyncTask.processVizFiles]: {
    onStart: handleClearData,
    onSuccess: handleVizFileData,
  },
}
// Action creators are generated for each case reducer function
export const {
  setScenarioName,
  setUploadFilename,
  setTaskUrl,
  loadData,
  clearData,
  clearReports,
  confirmSettings,
  updateTracksBaseline,
  updateFuelScenarioSettings,
  updateEndUseScenarioSettings,
  updateSolarScenarioSettings,
  setMetric,
  setNormalized,
  setBreakdown,
  setSelectedScenario,
  setGraphMode,
  setTarget,
  setTargetMetric,
  updateTimeSeriesSetting,
  updateSettingsPageTab,
  updateScenarioSettingsPageGroup,
  setBaselineAssignment,
  setEcmAssignment,
  setAdoptionOccupation,
  setAdoptionProbability,
  setAdoptionMode,
  setAdoptionGisIdColumn,
  setAdoptionCacheKey,
} = analysisSlice.actions

export default analysisSlice.reducer
